import { tryFetchRetry } from '@repo/editor/src/utils/fetchRetry';
import qs from 'qs';

import { STRAPI_API_URL, STRAPI_AUTH_KEY } from 'strapi/constants';
import { pageApiUrl } from 'strapi/page/config';
import { Category, PageType } from 'strapi/page/types';
import {
  BaseStrapiPage,
  Populate,
  RequestParams,
  StrapiComponentType,
  StrapiFilter,
  StrapiResponse,
} from 'strapi/types';
import { buildRequest, normalize } from 'strapi/utils';

const request = async <T extends object>(
  page: PageType,
  filter: StrapiFilter
): Promise<
  T[] | T | { success: false; error: string; errorObject?: Error }
> => {
  const params = buildRequest(page, filter);
  const url = pageApiUrl[page];
  if (!url || !params) {
    return { success: false, error: `Unexpected page type: ${page}` };
  }
  const request = qs.stringify(params);
  const response = await tryFetchRetry<StrapiResponse<T>>(
    `${STRAPI_API_URL}/api/${url}?${request}`,
    {
      method: 'GET',
      headers: {
        Authorization: `bearer ${STRAPI_AUTH_KEY}`,
      },
      cache: 'force-cache',
    }
  );

  if ('error' in response) {
    return response;
  }

  if ('message' in response) {
    return {
      success: false,
      error: `Response message is: ${response.message}`,
    };
  }

  if (!('data' in response)) {
    return {
      success: false,
      error: 'Unknown error',
    };
  }

  return normalize(response.data as StrapiComponentType<T>) as T[] | T;
};

export const getSingle = async <T extends object>(
  page: PageType,
  filter: StrapiFilter
): Promise<T | { success: false; error: string; errorObject?: Error }> => {
  const response = await request<T>(page, filter);

  if ('error' in response) {
    return response;
  }

  if (Array.isArray(response)) {
    if (!response || response.length !== 1) {
      return {
        success: false,
        error: `Unexpected items response: ${
          response?.length ?? 0
        } for page: ${page}, filter: ${JSON.stringify(filter)}`,
      };
    }

    return response[0];
  }

  return response;
};

export const getFields = async <T extends object>(
  page: PageType,
  fields: string[],
  filters?: StrapiFilter,
  populate?: Populate<T>
): Promise<
  T | T[] | { success: false; error: string; errorObject?: Error }
> => {
  const url = pageApiUrl[page];
  if (!url) {
    return { success: false, error: `Unexpected page type: ${page}` };
  }

  const request = qs.stringify({ fields, filters, populate });
  const response = await tryFetchRetry<StrapiResponse<T>>(
    `${STRAPI_API_URL}/api/${url}?${request}`,
    {
      method: 'GET',
      headers: {
        Authorization: `bearer ${STRAPI_AUTH_KEY}`,
      },
    }
  );

  if (!response || 'error' in response) {
    return response;
  }

  return normalize(response.data as StrapiComponentType<T>) as T[] | T;
};

export const getFieldsV2 = async <T extends object>(options: {
  pageKey: string;
  fields: string[];
  filters?: StrapiFilter;
  populate?: Populate<T>;
}): Promise<
  T | T[] | { success: false; error: string; errorObject?: Error }
> => {
  const request = qs.stringify({
    fields: options.fields,
    filters: options.filters,
    populate: options.populate,
  });
  const response = await tryFetchRetry<StrapiResponse<T>>(
    `${STRAPI_API_URL}/api/${options.pageKey}?${request}`,
    {
      method: 'GET',
      headers: {
        Authorization: `bearer ${STRAPI_AUTH_KEY}`,
      },
    }
  );

  if (!response || 'error' in response) {
    return response;
  }

  return normalize(response.data as StrapiComponentType<T>) as T[] | T;
};

export const getCategories = async (
  populate: RequestParams<Category>
): Promise<
  Category[] | { success: false; error: string; errorObject?: Error }
> => {
  const categoriesApiEndpoint = 'categories';
  const request = qs.stringify(populate);
  const response = await tryFetchRetry<StrapiResponse<Category>>(
    `${STRAPI_API_URL}/api/${categoriesApiEndpoint}?${request}`,
    {
      method: 'GET',
      headers: {
        Authorization: `bearer ${STRAPI_AUTH_KEY}`,
      },
    }
  );

  if (!response || 'error' in response) {
    return response;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  return normalize(response.data as any) as Category[];
};

export const basicStrapiRequest = async <T>(options: {
  pageKey: string;
  requestObject: RequestParams<T>;
}) => {
  const request = qs.stringify(options.requestObject);

  const result = await tryFetchRetry<BaseStrapiPage<T>>(
    `${STRAPI_API_URL}/api/${options.pageKey}?${request}`,
    {
      method: 'GET',
      headers: {
        Authorization: `bearer ${STRAPI_AUTH_KEY}`,
      },
      cache: 'force-cache',
    }
  );
  if ('error' in result) {
    return result;
  }
  return normalize(result) as T[];
};
